import type { CoverageLookups } from './coverageReport'
import { DETAIL_STATUS_LABEL, type DetailedOutFilters, type DetailedOutRow } from './detailedOutFigures'
import type { ExportTable } from './reportExport'
import { ROSTER_STATUS_LABEL, type RosterFilters, type RosterRow } from './rosterFigures'
import { supervisorLabel } from './supervisorLabel'

type Lookups = Pick<CoverageLookups, 'teams' | 'supervisors'>

function commonParameters(filters: { teamId?: string; supervisorId?: string; search?: string }, lookups: Lookups) {
  return [
    { label: 'Team', value: filters.teamId ? (lookups.teams.get(filters.teamId) ?? 'Unknown') : 'All' },
    { label: 'Supervisor', value: filters.supervisorId ? supervisorLabel(filters.supervisorId, lookups) : 'All' },
    { label: 'Employee search', value: filters.search ? filters.search : 'None' },
  ]
}

export function detailedOutExportTable(
  rows: DetailedOutRow[],
  totalDays: number,
  input: { from: string; to: string; filters: DetailedOutFilters; lookups: Lookups; generatedAt: Date },
): ExportTable {
  return {
    title: 'Detailed-Out Report',
    parameters: [
      { label: 'Date range', value: `${input.from} to ${input.to}` },
      ...commonParameters(input.filters, input.lookups),
      { label: 'Status', value: input.filters.status ? DETAIL_STATUS_LABEL[input.filters.status] : 'All' },
    ],
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Employee', width: 22 },
      { header: 'Detail type' },
      { header: 'Originating team' },
      { header: 'Destination', width: 24 },
      { header: 'Start date', width: 12 },
      { header: 'End date', width: 12 },
      { header: 'Status' },
      { header: 'Days in range', numeric: true },
      { header: 'Effect on available manpower', width: 28 },
    ],
    rows: rows.map((row) => [
      row.employee,
      row.detailType,
      row.team,
      row.destination,
      row.startDate,
      row.endDate,
      DETAIL_STATUS_LABEL[row.status],
      row.daysInRange,
      row.effect,
    ]),
    totals: { label: 'Total', values: [null, null, null, null, null, null, null, totalDays, null] },
  }
}

export function rosterExportTable(
  rows: RosterRow[],
  input: { filters: RosterFilters; lookups: Lookups; asOf: string; generatedAt: Date },
): ExportTable {
  return {
    title: 'Seniority Roster',
    parameters: [
      { label: 'As of', value: input.asOf },
      ...commonParameters(input.filters, input.lookups),
      { label: 'Shift', value: input.filters.shiftName ?? 'All' },
      { label: 'Status', value: input.filters.status ? ROSTER_STATUS_LABEL[input.filters.status] : 'All' },
    ],
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Seniority', numeric: true, leftAlign: true, width: 10 },
      { header: 'Employee', width: 22 },
      { header: 'Identifier' },
      { header: 'Seniority date', width: 14 },
      { header: 'Team' },
      { header: 'Title', width: 22 },
      { header: 'Shift' },
      { header: 'Qualifications', width: 32 },
      { header: 'Status' },
    ],
    rows: rows.map((row) => [
      row.rank,
      row.employee,
      row.identifier,
      row.seniorityDate,
      row.team,
      row.title,
      row.shift,
      row.qualifications.join(', '),
      ROSTER_STATUS_LABEL[row.status],
    ]),
  }
}
